import React from 'react';

import { useDiagnosisFormField } from '../../../../widgets';
import { COMMON_DIAGNOSES } from '../../../../components/procedures/widget/dental/commonDiagnoses';
import { STATUS_COLORS } from '../../../../components/procedures/widget/dental/statusColors';

const SURFACE_KEYS = ['M', 'D', 'B', 'L', 'O'];
const SEVERITY_FILL = {
  mild:     STATUS_COLORS.watch,
  moderate: STATUS_COLORS.diagnosis,
  severe:   STATUS_COLORS.urgent,
};

/**
 * ToothDiagnosisExtras — dental plugin contribution to DiagnosisWidget.
 *
 * Renders inside the widget body via `opts.extras`:
 *   - Common-diagnosis quick chips (one tap fills text + severity)
 *   - Surface picker M/D/B/L/O (single-tooth, single-token only)
 *
 * Bundles both because they share the same widget mount and a single
 * extras slot is the MVP extension mechanism. When the proper
 * `<ExtensionSlot slot="...">` registry lands (plugin #2), split into
 * `ToothCommonDiagnoses` + `ToothSurfacePicker` and register
 * independently.
 *
 * Reads + writes widget form state via the public
 * `useDiagnosisFormField` hook. The widget body owns the actual state;
 * this component is the renderer + setter chooser.
 *
 * Props:
 *   showSurfaces — boolean; false in bulk-edit mode (multi-token or
 *                  quadrant/FM selection) where per-surface scoping
 *                  doesn't make sense. ToothActionsPanel computes this
 *                  based on the token list.
 */
export default function ToothDiagnosisExtras({ showSurfaces = true }) {
  const [content, setContent] = useDiagnosisFormField('content');
  const [severity, setSeverity] = useDiagnosisFormField('severity');
  const [surfaces, setSurfaces] = useDiagnosisFormField('surfaces');

  const applyCommonDiagnosis = (entry) => {
    const prev = (content || '').trim();
    if (!prev) setContent(entry.label);
    else if (!prev.toLowerCase().includes(entry.label.toLowerCase())) {
      setContent(`${prev} · ${entry.label}`);
    }
    if (entry.severity) setSeverity(entry.severity);
  };

  const toggleSurface = (s) => {
    const list = Array.isArray(surfaces) ? surfaces : [];
    if (list.includes(s)) setSurfaces(list.filter((x) => x !== s));
    else setSurfaces([...list, s]);
  };
  const clearSurfaces = () => setSurfaces([]);

  return (
    <div className="flex flex-col gap-2">
      {/* Common diagnoses */}
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        <span className="opacity-70 mr-0.5">Common:</span>
        {COMMON_DIAGNOSES.map((entry) => (
          <button
            key={entry.label}
            type="button"
            onClick={() => applyCommonDiagnosis(entry)}
            className="px-2 py-0.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
            title={entry.severity ? `Fills + sets severity: ${entry.severity}` : 'Fills diagnosis'}
            style={entry.severity ? { borderColor: SEVERITY_FILL[entry.severity] } : undefined}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {/* Surface picker */}
      {showSurfaces && (
        <div className="flex flex-wrap items-center gap-1 text-[11px]">
          <span className="opacity-70 mr-0.5">Surface:</span>
          {SURFACE_KEYS.map((s) => {
            const active = Array.isArray(surfaces) && surfaces.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSurface(s)}
                className={[
                  'w-8 py-0.5 rounded-md border text-center font-semibold',
                  active
                    ? 'bg-teal-600 text-white border-teal-700'
                    : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700',
                ].join(' ')}
                title={`Toggle ${s} surface`}
              >
                {s}
              </button>
            );
          })}
          <button
            type="button"
            onClick={clearSurfaces}
            disabled={!Array.isArray(surfaces) || surfaces.length === 0}
            className="px-2 py-0.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
          >
            Whole tooth
          </button>
        </div>
      )}

      {/* Reflect the severity selection (the widget's built-in severity
          row also renders this — but mirroring it here as a read-only
          hint makes the quick-chip → severity link visible). */}
      {severity && (
        <div className="text-[10px] opacity-60">
          Current severity: <strong>{severity}</strong>
        </div>
      )}
    </div>
  );
}
